/**
 * ArchiMate 3.1 → 4 type-merge — de gedeelde mapping-tabel.
 *
 * **Waarom deze module bestaat**: The Open Group's ArchiMate 4 Specification
 * (C260, april 2026) voegt alle laag-specifieke gedragselementen samen tot
 * generieke elementen — drie soorten Process worden één `Process`, drie
 * soorten Service worden één `Service`, enzovoort. Het onderscheid dat
 * daarmee uit het metamodel verdwijnt, wordt teruggewonnen via
 * specialisatie-profielen (C260 hoofdstuk 14), wat in deze codebase
 * stereotypes heet.
 *
 * Elk gemigreerd concept krijgt dus een **compat-stereotype** dat z'n
 * oorspronkelijke 3.1-identiteit draagt: een `archimate.BusinessProcess`
 * wordt een `archimate.Process` mét `«archimate31.BusinessProcess»`. Dat
 * maakt de conversie verliesvrij in beide richtingen — de v3.1↔v4-
 * weergaveschakelaar, de AMEFF-export en de PlantUML-emitter rekenen alle
 * drie terug naar het legacy-type via `legacyTypeIdFor`.
 *
 * **De labels en definities zijn geen eigen bedenksel**: C260 Tabel 14-2
 * noemt «Business Process», «Application Service» etc. expliciet als
 * voorbeeld-specialisaties mét hun oorspronkelijke 3.2-definitie. Het
 * compat-pack neemt die over, wat de stereotypes uitwisselbaar maakt met
 * elke andere tool die dezelfde tabel volgt.
 *
 * **Vijf consumenten** delen deze ene tabel — verander 'm hier, niet
 * daar: de D1-migratie (`0041`), AMEFF-import, AMEFF-export,
 * snapshot-normalisatie bij restore, en de PlantUML-parser/emitter.
 *
 * Zie `docs/architecture/archimate4-migratie.md` voor de volledige
 * fasering en de onderbouwing per keuze.
 */

/** Prefix van elk compat-stereotype. Bewust `archimate31` (niet `archimate3`)
 *  omdat het AMEFF-uitwisselformaat waar we naartoe blijven exporteren
 *  formeel de ArchiMate 3.1 Model Exchange File Format-standaard is (C19C) —
 *  C260 §1.1 verwijst daar zelf naar; er ís geen 4.0-uitwisselformaat. */
export const COMPAT_PREFIX = "archimate31.";

/** Eén mapping-regel: waar een 3.1-type naartoe gaat, en welk stereotype
 *  z'n oorspronkelijke identiteit bewaart. */
export interface TypeMigration {
  /** Het generieke ArchiMate-4-type dat het concept krijgt. */
  typeId: string;
  /** Het compat-stereotype dat de 3.1-identiteit vasthoudt. */
  stereotype: string;
  /** true wanneer C260 méér dan één doeltype toestaat en wij een default
   *  kiezen. De modelleur kan achteraf hertyperen via de type-wijzig-
   *  dialoog; het review-scherm in fase 4 lijst deze gevallen op. */
  ambiguous?: boolean;
  /** De alternatieven die C260 óók toestaat — puur documentatie plus de
   *  kandidatenlijst voor het review-scherm. */
  alternatives?: readonly string[];
}

/**
 * De 18 types die samenvloeien. Geverifieerd tegen `archimate-pack.json`:
 * elke sleutel bestaat vandaag als eersteklas type.
 *
 * **Geen rename-ketens**: geen enkele waarde uit `typeId` komt óók voor als
 * sleutel. Daardoor is de volgorde van de UPDATE-statements in de
 * D1-migratie irrelevant en is elke stap los idempotent.
 */
export const TYPE_MAP_31_TO_4: Readonly<Record<string, TypeMigration>> = {
  // ── Process (3 → 1) ────────────────────────────────────────────────
  "archimate.BusinessProcess": {
    typeId: "archimate.Process",
    stereotype: "archimate31.BusinessProcess",
  },
  "archimate.ApplicationProcess": {
    typeId: "archimate.Process",
    stereotype: "archimate31.ApplicationProcess",
  },
  // Interaction is in C260 geschrapt (slecht begrepen, zelden gebruikt).
  // De spec schrijft Process voor, uitgevoerd door een Collaboration; het
  // "meerdere uitvoerders"-aspect legt de modelleur vast met multipliciteit
  // ≥ 2 op de Assignment. Die tweede stap is inhoudelijk werk per geval —
  // de conversie doet alleen stap 1.
  //
  // Alle drie de smaken staan hier, ook al had onze eigen pack er maar één
  // (`ApplicationInteraction`). Dat scheelde niets bij de flip van ons eigen
  // model, maar het is wél nodig bij import: een gewoon Archi-3.x-bestand met
  // een Business Interaction erin zou anders binnenkomen als
  // `archimate.BusinessInteraction` — een type dat bij ons niet bestaat, en de
  // import valideert het type niet.
  "archimate.BusinessInteraction": {
    typeId: "archimate.Process",
    stereotype: "archimate31.BusinessInteraction",
  },
  "archimate.ApplicationInteraction": {
    typeId: "archimate.Process",
    stereotype: "archimate31.ApplicationInteraction",
  },
  "archimate.TechnologyInteraction": {
    typeId: "archimate.Process",
    stereotype: "archimate31.TechnologyInteraction",
  },

  // ── Function (2 → 1) ───────────────────────────────────────────────
  "archimate.BusinessFunction": {
    typeId: "archimate.Function",
    stereotype: "archimate31.BusinessFunction",
  },
  "archimate.ApplicationFunction": {
    typeId: "archimate.Function",
    stereotype: "archimate31.ApplicationFunction",
  },

  // ── Service (3 → 1) ────────────────────────────────────────────────
  "archimate.BusinessService": {
    typeId: "archimate.Service",
    stereotype: "archimate31.BusinessService",
  },
  "archimate.ApplicationService": {
    typeId: "archimate.Service",
    stereotype: "archimate31.ApplicationService",
  },
  "archimate.TechnologyService": {
    typeId: "archimate.Service",
    stereotype: "archimate31.TechnologyService",
  },

  // ── Event (3 → 1) ──────────────────────────────────────────────────
  "archimate.BusinessEvent": {
    typeId: "archimate.Event",
    stereotype: "archimate31.BusinessEvent",
  },
  "archimate.ApplicationEvent": {
    typeId: "archimate.Event",
    stereotype: "archimate31.ApplicationEvent",
  },
  "archimate.ImplementationEvent": {
    typeId: "archimate.Event",
    stereotype: "archimate31.ImplementationEvent",
  },

  // ── Collaboration (2 → 1) ──────────────────────────────────────────
  "archimate.BusinessCollaboration": {
    typeId: "archimate.Collaboration",
    stereotype: "archimate31.BusinessCollaboration",
  },
  "archimate.ApplicationCollaboration": {
    typeId: "archimate.Collaboration",
    stereotype: "archimate31.ApplicationCollaboration",
  },

  // ── Role (1 → 1, maar generiek geworden) ───────────────────────────
  // Role is in v4 een Common-Domain-element: elk intern active-structure-
  // element kan er via Assignment gedrag aan koppelen. Dat is precies wat
  // nodig is om mens, systeem en AI-agent als uitwisselbare uitvoerders
  // van hetzelfde proces te modelleren.
  "archimate.BusinessRole": {
    typeId: "archimate.Role",
    stereotype: "archimate31.BusinessRole",
  },

  // ── Geschrapte types (4) ───────────────────────────────────────────
  // Constraint overlapt met Requirement. Semantisch verlies: het
  // ingebouwde onderscheid tussen "wat moet worden bereikt" en "wat de
  // oplossingsruimte beperkt". Het profiel is hier dus essentieel, niet
  // cosmetisch. Aspect-behoudend (beide `motivation`).
  "archimate.Constraint": {
    typeId: "archimate.Requirement",
    stereotype: "archimate31.Constraint",
  },
  // Contract overlapt met Business Object. Aspect-behoudend (beide
  // `passive_structure`), dus geen matrix-impact.
  "archimate.Contract": {
    typeId: "archimate.BusinessObject",
    stereotype: "archimate31.Contract",
  },
  // Gap overlapt met Assessment én Deliverable. Wij kiezen Assessment als
  // default omdat dat aspect-behoudend is — `archimate.Gap` heeft in deze
  // pack `aspect: "motivation"`, net als Assessment — waardoor de
  // aspect-gedreven matrixregels (Access/Influence/Triggering) ongemoeid
  // blijven. Deliverable zou dat aspect breken.
  "archimate.Gap": {
    typeId: "archimate.Assessment",
    stereotype: "archimate31.Gap",
    ambiguous: true,
    alternatives: ["archimate.Deliverable"],
  },
  // Representation overlapt met de passieve structuurelementen. C260 noemt
  // Data Object, Artifact en Material; DataObject is de gangbare casus
  // (documenten, rapporten) én aspect-behoudend.
  "archimate.Representation": {
    typeId: "archimate.DataObject",
    stereotype: "archimate31.Representation",
    ambiguous: true,
    alternatives: ["archimate.Artifact", "archimate.Material"],
  },
};

/**
 * Terugweg: welk 3.1-type hoort bij welk compat-stereotype. Afgeleid uit
 * `TYPE_MAP_31_TO_4` zodat er maar één plek is om te onderhouden.
 */
export const LEGACY_TYPE_BY_COMPAT_STEREOTYPE: Readonly<Record<string, string>> =
  Object.fromEntries(
    Object.entries(TYPE_MAP_31_TO_4).map(([legacyTypeId, m]) => [m.stereotype, legacyTypeId]),
  );

/**
 * Default-legacy-type per generiek v4-type, voor concepten die in v4-modus
 * zijn aangemaakt en dus géén compat-stereotype dragen ("born-v4").
 *
 * Nodig omdat AMEFF 3.1 blijft: een `archimate.Process` zonder compat
 * bestáát niet in het uitwisselformaat en moet als iets weggeschreven
 * worden. De business-variant is de veiligste gok — het is de meest
 * voorkomende en de minst verrassende voor een ontvangende tool. De export
 * schrijft daarnaast `ae:original_type` mee, zodat een re-import in déze
 * app het generieke type terugkrijgt in plaats van de gok.
 */
export const BORN_V4_EXPORT_DEFAULT: Readonly<Record<string, string>> = {
  "archimate.Process": "archimate.BusinessProcess",
  "archimate.Function": "archimate.BusinessFunction",
  "archimate.Service": "archimate.BusinessService",
  "archimate.Event": "archimate.BusinessEvent",
  "archimate.Collaboration": "archimate.BusinessCollaboration",
  "archimate.Role": "archimate.BusinessRole",
};

/** true wanneer dit stereotype-id een 3.1-compat-marker is. */
export function isCompatStereotype(stereotypeId: string): boolean {
  return stereotypeId.startsWith(COMPAT_PREFIX);
}

/**
 * Een leesbare specialisatie-naam terug naar ons compat-stereotype.
 *
 * Nodig voor bestanden in de *v4-dialect*: die zetten het generieke type in
 * `xsi:type` en de specialisatie in een gewone property — met de conventionele
 * naam, dus "Business Process" en niet `archimate31.BusinessProcess`. Zonder
 * deze vertaling landt zo'n bestand bij ons als kaal generiek type en is de
 * laag-informatie stil verdwenen.
 *
 * De vergelijking loopt over de id's zelf (`archimate31.BusinessProcess` →
 * "business process") in plaats van over een labeltabel: dan kan 'ie niet uit
 * de pas lopen met de pack, en werkt 'ie voor elk compat-stereotype dat er ooit
 * bijkomt. Guillemets, hoofdletters en extra spaties maken niet uit.
 */
export function compatStereotypeForLabel(label: string): string | undefined {
  const genormaliseerd = normaliseerSpecialisatieNaam(label);
  if (!genormaliseerd) return undefined;
  for (const migration of Object.values(TYPE_MAP_31_TO_4)) {
    const id = migration.stereotype;
    if (normaliseerSpecialisatieNaam(id.slice(COMPAT_PREFIX.length)) === genormaliseerd) return id;
  }
  return undefined;
}

/** "«Business Process»" en "archimate31.BusinessProcess" worden allebei
 *  "business process". */
function normaliseerSpecialisatieNaam(waarde: string): string {
  return waarde
    .replace(COMPAT_PREFIX, " ")
    .replace(/[«»]/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Het compat-stereotype dat bij een 3.1-type hoort, of `undefined` als
 *  het type niet meedoet aan de merge. */
export function compatStereotypeFor(legacyTypeId: string): string | undefined {
  return TYPE_MAP_31_TO_4[legacyTypeId]?.stereotype;
}

/** Splitst een stereotype-array in de compat-marker(s) en de rest.
 *  Gebruikt door de weergaveschakelaar (fase 3) en de export (fase 5). */
export function splitCompatStereotypes(stereotypes: readonly string[]): {
  compat: string[];
  extension: string[];
} {
  const compat: string[] = [];
  const extension: string[] = [];
  for (const s of stereotypes) {
    if (isCompatStereotype(s)) compat.push(s);
    else extension.push(s);
  }
  return { compat, extension };
}

/**
 * Rekent een (v4-type + stereotypes) terug naar het 3.1-type waar het
 * vandaan kwam. Dit is wat AMEFF-export, de PlantUML-emitter en de
 * v3.1-weergavemodus alle drie nodig hebben.
 *
 * Volgorde:
 *  1. Een compat-stereotype wint altijd — dat is de opgeslagen herkomst.
 *  2. Geen compat maar wel een generiek v4-type ("born-v4") → de
 *     gepubliceerde default uit `BORN_V4_EXPORT_DEFAULT`.
 *  3. Anders: het type is nooit gemergd (Node, BusinessActor, …) en is
 *     in 3.1 en 4 identiek — geef 'm ongewijzigd terug.
 */
export function legacyTypeIdFor(typeId: string, stereotypes: readonly string[] = []): string {
  for (const s of stereotypes) {
    const legacy = LEGACY_TYPE_BY_COMPAT_STEREOTYPE[s];
    if (legacy) return legacy;
  }
  return BORN_V4_EXPORT_DEFAULT[typeId] ?? typeId;
}

/**
 * AMEFF-export: welk 3.1-`xsi:type` hoort bij dit concept, en moeten we
 * erbij vertellen dat het een gok was?
 *
 * AMEFF blijft ArchiMate 3.1 — C260 verwijst zelf terug naar C19C en er ís
 * geen 4.0-uitwisselformaat. Elk concept moet dus als een 3.1-type naar
 * buiten. Twee gevallen:
 *
 * - **Gemigreerd** (compat-stereotype aanwezig): het oude type is bekend,
 *   dus dit is geen gok. Verliesvrij.
 * - **Born-v4** (aangemaakt ná de merge): het generieke type bestáát niet in
 *   3.1, dus we kiezen de business-variant — meest voorkomend, minst
 *   verrassend voor een ontvangende tool. `originalType` gaat dan mee zodat
 *   een re-import in DÉZE app het generieke type terugkrijgt in plaats van
 *   de gok. Archi negeert die property.
 *
 * **Waarom dit hier staat en niet in de handler**: import en export moeten
 * elkaars inverse zijn, en een test moet dat kunnen aantonen. Stond de
 * vertaling in de worker-handler, dan zou een domein-test 'm moeten
 * naschrijven — en dan toets je je eigen kopie in plaats van de code die
 * draait. Precies zo ontstond de fout die deze functie oplost: de
 * born-v4-tak in de handler was onbereikbaar omdat `legacyTypeIdFor` al
 * intern terugvalt op `BORN_V4_EXPORT_DEFAULT`.
 */
export function ameffTypeForExport(
  typeId: string,
  stereotypes: readonly string[] = [],
): { xsiType: string; originalType?: string } | null {
  if (!typeId.startsWith("archimate.")) return null;
  const legacy = legacyTypeIdFor(typeId, stereotypes);
  const xsiType = legacy.slice("archimate.".length);
  if (legacy === typeId) return { xsiType };
  // Het type is vertaald. Kwam die vertaling uit een compat-stereotype, dan
  // is 't de echte herkomst; anders is 't de born-v4-gok en moet dat erbij.
  return stereotypes.some(isCompatStereotype) ? { xsiType } : { xsiType, originalType: typeId };
}

/**
 * AMEFF-import: welk intern type hoort bij dit `xsi:type`?
 *
 * De inverse van {@link ameffTypeForExport}. Een binnenkomend 3.1-type wordt
 * altijd omgezet naar v4 mét compat-stereotype — er is één model, en dat is
 * v4 (beslissing 2026-08-08). `originalType` uit `ae:original_type` wint,
 * want dat is onze eigen export die z'n echte type meestuurde.
 */
export function conceptTypeForAmeffImport(
  xsiType: string,
  originalType?: string | undefined,
): { typeId: string; stereotypes: string[] } {
  const legacyTypeId = `archimate.${xsiType}`;
  const migration = TYPE_MAP_31_TO_4[legacyTypeId];
  const base = migration
    ? { typeId: migration.typeId, stereotypes: [migration.stereotype] }
    : { typeId: legacyTypeId, stereotypes: [] as string[] };
  if (!originalType) return base;
  // Een hersteld generiek type draagt geen 3.1-identiteit: het is ná de merge
  // aangemaakt. Het compat-stereotype dat uit de gok volgde moet dus weg.
  return {
    typeId: originalType,
    stereotypes: isV4GenericType(originalType)
      ? base.stereotypes.filter((s) => !isCompatStereotype(s))
      : base.stereotypes,
  };
}

/** Minimale vorm die `normalizeConcept` nodig heeft — bewust structureel
 *  getypeerd zodat zowel domein-`Concept`s als kale snapshot-JSON-objecten
 *  erdoorheen kunnen. */
export interface MigratableConcept {
  typeId: string;
  stereotypes?: string[];
}

/**
 * Zet één concept om naar het v4-compat-schema: generiek type + compat-
 * stereotype. **Idempotent** — een al gemigreerd concept komt ongewijzigd
 * terug, en een dubbel toegepast stereotype wordt niet nogmaals toegevoegd.
 *
 * Gebruikt bij snapshot-restore (fase 4): historische R2-blobs worden
 * bewust NIET herschreven maar bij het teruglezen genormaliseerd — zelfde
 * patroon als `normalizeViewState`. Rewriting van historische blobs is
 * traag, risicovol en onnodig.
 *
 * Retourneert een nieuw object; muteert de input niet.
 */
export function normalizeConcept<T extends MigratableConcept>(concept: T): T {
  const mapping = TYPE_MAP_31_TO_4[concept.typeId];
  if (!mapping) return concept;

  const current = concept.stereotypes ?? [];
  const stereotypes = current.includes(mapping.stereotype)
    ? [...current]
    : [...current, mapping.stereotype];

  return { ...concept, typeId: mapping.typeId, stereotypes };
}

/** Alle 3.1-typeIds die door de merge geraakt worden. */
export function legacyTypeIds(): string[] {
  return Object.keys(TYPE_MAP_31_TO_4);
}

/** De generieke v4-typeIds die de merge oplevert (ontdubbeld). */
export function mergedTargetTypeIds(): string[] {
  return [...new Set(Object.values(TYPE_MAP_31_TO_4).map((m) => m.typeId))];
}

/**
 * De zes generieke Common-Domain-types die de migratie **nieuw** introduceert.
 *
 * **Waarom dit apart staat van `mergedTargetTypeIds()`**: dat zijn er tien, en
 * de vier andere (`Requirement`, `BusinessObject`, `Assessment`, `DataObject`)
 * bestonden allang als gewone ArchiMate-types. Wie een merge-*doel* behandelt
 * als "nieuw in v4" verbergt dus per ongeluk vier kern-types die niets met de
 * migratie te maken hebben. Precies dat ging fout in de eerste versie van de
 * palette-omschakeling: in v3.1-modus verdwenen Business Object, Requirement,
 * Assessment en Data Object uit het palette (2026-08-09, gevonden door Gerald).
 *
 * Gebruik deze lijst overal waar je bedoelt "types die er vóór de merge nog
 * niet waren" — niet `mergedTargetTypeIds()`. Een drift-guard-test controleert
 * dat dit exact de merge-doelen op de laag `common` zijn.
 */
export const V4_GENERIC_TYPE_IDS: readonly string[] = [
  "archimate.Process",
  "archimate.Function",
  "archimate.Service",
  "archimate.Event",
  "archimate.Collaboration",
  "archimate.Role",
];

/** true wanneer `typeId` pas met ArchiMate 4 is geïntroduceerd. */
export function isV4GenericType(typeId: string): boolean {
  return V4_GENERIC_TYPE_IDS.includes(typeId);
}

/** De 3.1-typeIds waarvoor C260 meer dan één doeltype toestaat en wij een
 *  default kozen — de invoer voor het review-scherm in fase 4. */
export function ambiguousLegacyTypeIds(): string[] {
  return Object.entries(TYPE_MAP_31_TO_4)
    .filter(([, m]) => m.ambiguous)
    .map(([legacyTypeId]) => legacyTypeId);
}

/** De kandidaat-doeltypen voor één ambigue conversie: het gekozen default
 *  eerst, daarna de alternatieven die C260 óók toestaat. */
export interface AmbiguousConversion {
  /** Het 3.1-type waar dit vandaan komt (`archimate.Gap`, …). */
  legacyTypeId: string;
  /** Het compat-stereotype dat de 3.1-identiteit vasthoudt. */
  stereotype: string;
  /** Kandidaten in presentatievolgorde; `[0]` is de gekozen default. */
  candidates: readonly string[];
}

/**
 * Zoek de ambigue conversie op via het compat-stereotype dat een geconverteerd
 * concept draagt. Dat is de sleutel die de UI in handen heeft — het 3.1-type
 * bestaat na de conversie immers niet meer op de rij.
 *
 * Retourneert `null` voor elk niet-ambigu (of onbekend) stereotype, zodat een
 * caller ongefilterd elke stereotype-lijst kan doorlopen.
 */
export function ambiguousConversionFor(stereotype: string): AmbiguousConversion | null {
  for (const [legacyTypeId, m] of Object.entries(TYPE_MAP_31_TO_4)) {
    if (m.stereotype !== stereotype) continue;
    if (!m.ambiguous) return null;
    return {
      legacyTypeId,
      stereotype: m.stereotype,
      candidates: [m.typeId, ...(m.alternatives ?? [])],
    };
  }
  return null;
}

/** Eerste ambigue conversie in een stereotype-lijst, of `null`. Een concept
 *  draagt er hooguit één — de compat-stereotypes sluiten elkaar uit. */
export function ambiguousConversionIn(stereotypes: readonly string[]): AmbiguousConversion | null {
  for (const s of stereotypes) {
    const hit = ambiguousConversionFor(s);
    if (hit) return hit;
  }
  return null;
}
